import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import CleaningScheduleService from '../../services/cleaningSchedule.service';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function CleaningScheduleAvailabilityDialog({
  open,
  onClose,
  employees,
  weekStart,
  weekEnd,
  defaultServiceDate,
  canEdit,
  onSaved,
  onNotify,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [empWeeklyId, setEmpWeeklyId] = useState('');
  const [weeklyHours, setWeeklyHours] = useState(() => {
    const m = {};
    for (let w = 1; w <= 7; w += 1) m[w] = '8';
    return m;
  });
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [savingWeekly, setSavingWeekly] = useState(false);

  const [unavailList, setUnavailList] = useState([]);
  const [loadingUnavail, setLoadingUnavail] = useState(false);
  const [savingUnavail, setSavingUnavail] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [offEmployeeId, setOffEmployeeId] = useState('');
  const [offDate, setOffDate] = useState('');
  const [offFullDay, setOffFullDay] = useState(true);
  const [offTimeStart, setOffTimeStart] = useState('09:00');
  const [offTimeEnd, setOffTimeEnd] = useState('17:00');
  const [offReason, setOffReason] = useState('');

  const activeEmployees = useMemo(
    () =>
      (Array.isArray(employees) ? employees : []).filter(
        (e) => !e?.status || String(e.status).toLowerCase() === 'active'
      ),
    [employees]
  );

  useEffect(() => {
    if (!open) return;
    setTab(0);
    const first = activeEmployees[0];
    setEmpWeeklyId(first ? String(first.id) : '');
    setOffEmployeeId(first ? String(first.id) : '');
    setOffDate(defaultServiceDate || weekStart || '');
    setOffFullDay(true);
    setOffReason('');
  }, [open, activeEmployees, defaultServiceDate, weekStart]);

  useEffect(() => {
    if (!open || !empWeeklyId) return;
    let cancelled = false;
    (async () => {
      setLoadingWeekly(true);
      try {
        const res = await CleaningScheduleService.getWeeklyAvailability({ employee_id: empWeeklyId });
        const rows = res.data?.data || [];
        const map = {};
        for (let wd = 1; wd <= 7; wd += 1) map[wd] = '';
        rows.forEach((r) => {
          const wd = Number(r.weekday);
          if (wd >= 1 && wd <= 7) map[wd] = String(r.hours_available ?? '');
        });
        const emp = activeEmployees.find((e) => String(e.id) === String(empWeeklyId));
        const fallback = emp?.max_hours_per_day != null ? String(emp.max_hours_per_day) : '8';
        for (let wd = 1; wd <= 7; wd += 1) {
          if (map[wd] === '' || map[wd] == null) map[wd] = fallback;
        }
        if (!cancelled) setWeeklyHours(map);
      } catch (e) {
        if (!cancelled) {
          onNotify?.(e.response?.data?.message || t('cleaningSchedule.availabilityLoadError'), 'error');
        }
      } finally {
        if (!cancelled) setLoadingWeekly(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, empWeeklyId, activeEmployees, t, onNotify]);

  const loadUnavailability = async () => {
    if (!weekStart || !weekEnd) return;
    setLoadingUnavail(true);
    try {
      const res = await CleaningScheduleService.getUnavailability({ week_start: weekStart, week_end: weekEnd });
      setUnavailList(res.data?.data || []);
    } catch (e) {
      onNotify?.(e.response?.data?.message || t('cleaningSchedule.availabilityLoadError'), 'error');
    } finally {
      setLoadingUnavail(false);
    }
  };

  useEffect(() => {
    if (!open || tab !== 1 || !weekStart || !weekEnd) return;
    loadUnavailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when week/tab/open changes
  }, [open, tab, weekStart, weekEnd]);

  const saveWeekly = async () => {
    if (!canEdit || !empWeeklyId) return;
    const availabilities = [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
      weekday,
      hours_available: Math.min(24, Math.max(0, parseFloat(weeklyHours[weekday]) || 0)),
    }));
    setSavingWeekly(true);
    try {
      await CleaningScheduleService.putWeeklyAvailability({
        employee_id: parseInt(empWeeklyId, 10),
        availabilities,
      });
      onNotify?.(t('cleaningSchedule.availabilityWeeklySaved'), 'success');
      onSaved?.();
    } catch (e) {
      onNotify?.(e.response?.data?.message || t('cleaningSchedule.availabilityLoadError'), 'error');
    } finally {
      setSavingWeekly(false);
    }
  };

  const addUnavailability = async () => {
    if (!canEdit || !offEmployeeId || !offDate) return;
    setSavingUnavail(true);
    try {
      const body = {
        employee_id: parseInt(offEmployeeId, 10),
        unavailable_date: offDate,
        is_full_day: offFullDay,
        reason: offReason || null,
      };
      if (!offFullDay) {
        body.time_start = offTimeStart?.length === 5 ? `${offTimeStart}:00` : offTimeStart;
        body.time_end = offTimeEnd?.length === 5 ? `${offTimeEnd}:00` : offTimeEnd;
      }
      await CleaningScheduleService.postUnavailability(body);
      onNotify?.(t('cleaningSchedule.availabilityTimeOffAdded'), 'success');
      await loadUnavailability();
      onSaved?.();
    } catch (e) {
      onNotify?.(e.response?.data?.message || t('cleaningSchedule.availabilityLoadError'), 'error');
    } finally {
      setSavingUnavail(false);
    }
  };

  const removeUnavailability = async (id) => {
    if (!canEdit || !id) return;
    setDeletingId(id);
    try {
      await CleaningScheduleService.deleteUnavailability(id);
      onNotify?.(t('cleaningSchedule.availabilityTimeOffRemoved'), 'success');
      await loadUnavailability();
      onSaved?.();
    } catch (e) {
      onNotify?.(e.response?.data?.message || t('cleaningSchedule.availabilityLoadError'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const employeeName = (id) => activeEmployees.find((e) => String(e.id) === String(id))?.name || `#${id}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('cleaningSchedule.availabilityDialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('cleaningSchedule.availabilityDialogIntro')}
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab label={t('cleaningSchedule.availabilityTabWeekly')} />
          <Tab label={t('cleaningSchedule.availabilityTabTimeOff')} />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="avail-emp-weekly">{t('cleaningSchedule.availabilityPickEmployee')}</InputLabel>
            <Select
              labelId="avail-emp-weekly"
              label={t('cleaningSchedule.availabilityPickEmployee')}
              value={empWeeklyId}
              onChange={(e) => setEmpWeeklyId(e.target.value)}
            >
              {activeEmployees.map((e) => (
                <MenuItem key={e.id} value={String(e.id)}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingWeekly ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3, 4, 5, 6, 7].map((wd) => (
                <TextField
                  key={wd}
                  size="small"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 24, step: 0.25 }}
                  label={t(`cleaningSchedule.availabilityDow${wd}`)}
                  value={weeklyHours[wd] ?? ''}
                  onChange={(e) => setWeeklyHours((prev) => ({ ...prev, [wd]: e.target.value }))}
                  disabled={!canEdit}
                />
              ))}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {t('cleaningSchedule.availabilityTimeOffHint')}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t('cleaningSchedule.availabilityTimeOffListTitle')}
          </Typography>
          {loadingUnavail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense disablePadding sx={{ mb: 2, maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {unavailList.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t('cleaningSchedule.availabilityTimeOffEmpty')} />
                </ListItem>
              ) : (
                unavailList.map((row) => (
                  <ListItem
                    key={row.id}
                    secondaryAction={
                      canEdit ? (
                        <IconButton edge="end" size="small" onClick={() => removeUnavailability(row.id)} disabled={deletingId === row.id}>
                          {deletingId === row.id ? <CircularProgress size={20} /> : <DeleteOutline fontSize="small" />}
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemText
                      primary={`${employeeName(row.employee_id)} · ${row.unavailable_date}`}
                      secondary={
                        row.is_full_day
                          ? t('cleaningSchedule.availabilityFullDay')
                          : `${row.time_start?.slice(0, 5) || '—'}–${row.time_end?.slice(0, 5) || '—'}`
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t('cleaningSchedule.availabilityAddTimeOffSection')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="avail-emp-off">{t('cleaningSchedule.availabilityPickEmployee')}</InputLabel>
              <Select
                labelId="avail-emp-off"
                label={t('cleaningSchedule.availabilityPickEmployee')}
                value={offEmployeeId}
                onChange={(e) => setOffEmployeeId(e.target.value)}
                disabled={!canEdit}
              >
                {activeEmployees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              type="date"
              label={t('cleaningSchedule.serviceDate')}
              value={offDate}
              onChange={(e) => setOffDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
            <FormControlLabel
              control={<Switch checked={offFullDay} onChange={(e) => setOffFullDay(e.target.checked)} disabled={!canEdit} />}
              label={t('cleaningSchedule.availabilityFullDay')}
            />
            {!offFullDay && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  type="time"
                  label={t('cleaningSchedule.availabilityTimeStart')}
                  value={offTimeStart}
                  onChange={(e) => setOffTimeStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEdit}
                />
                <TextField
                  size="small"
                  fullWidth
                  type="time"
                  label={t('cleaningSchedule.availabilityTimeEnd')}
                  value={offTimeEnd}
                  onChange={(e) => setOffTimeEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEdit}
                />
              </Box>
            )}
            <TextField
              size="small"
              fullWidth
              label={t('cleaningSchedule.availabilityReason')}
              value={offReason}
              onChange={(e) => setOffReason(e.target.value)}
              disabled={!canEdit}
            />
            <Button variant="contained" disabled={!canEdit || savingUnavail} onClick={addUnavailability}>
              {savingUnavail ? t('common.saving') : t('cleaningSchedule.availabilitySubmitTimeOff')}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {tab === 0 && canEdit && (
          <Button onClick={saveWeekly} disabled={savingWeekly || loadingWeekly || !empWeeklyId} variant="contained" color="primary">
            {savingWeekly ? t('common.saving') : t('cleaningSchedule.availabilitySaveWeekly')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
